import { SEAL_LABEL } from './vaultMotion';
import React, { useEffect, useState } from 'react';
import { Box, Button, FormControlLabel, Stack, Switch, TextField, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Close';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { removeMapConnection, updateMap, updateMapConnection } from '../../api/maps';
import { fetchWebhooks } from '../../api/webhooks';
import AskDialog from '../../components/AskDialog';
import ConsolePanel from '../../components/ConsolePanel';
import ScanFrame from '../../components/ScanFrame';
import StatusBadge from '../../components/StatusBadge';
import { useAuth } from '../../services/AuthContext';
import { C, FONT, hexToRgba, lcarsLabelSx } from '../../theme/tokens';
import { MapConnection, MapFolder, MapSystem, MassStatus, ShareScope } from '../../types';
import { UNFILED } from './CompartmentList';

const MASS_COLOR: Record<MassStatus, string> = {
  fresh: C.green,
  reduced: C.gold,
  critical: C.red,
  unknown: C.textDim,
};

/**
 * What used to occupy this slot: a two-line "no entry open" message inside
 * an otherwise-empty 340px column, for as long as nothing was selected —
 * which, on landing in a vault, is always. This is the same column doing
 * real work instead: what this vault actually holds (compartment
 * breakdown, home system), and the one thing that had no management
 * surface anywhere in the app at all — the connections a member logged
 * between two entries, previously visible only as diagram lines with no
 * way to remove a wrong one short of database surgery.
 */
const VaultSummary: React.FC<{
  vault: MapFolder;
  systems: MapSystem[];
  connections: MapConnection[];
  canDelete: boolean;
  isOwner: boolean;
  onDeleteVault: () => void;
}> = ({ vault, systems, connections, canDelete, isOwner, onDeleteVault }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { me } = useAuth();
  const home = systems.find((s) => s.is_home_system);
  const scopeKey = (vault.share_scope ?? 'private') as ShareScope;

  // Shares the ['admin','webhooks'] cache key with AdminPanel's WebhooksTab
  // — whichever loads first pays for the fetch. Only an admin/director can
  // reach the endpoint at all (see requireWebhookAdmin), so it's not worth
  // querying otherwise.
  const canSeeWebhooks = me.isAdmin || me.isDirector;
  const { data: allWebhooks } = useQuery({
    queryKey: ['admin', 'webhooks'],
    queryFn: fetchWebhooks,
    enabled: canSeeWebhooks,
  });
  const vaultWebhooks = (allWebhooks ?? []).filter((h) => h.scope_type === 'vault' && h.scope_id === vault.id);

  const byTag = new Map<string, number>();
  for (const s of systems) {
    const key = s.tag?.trim() || UNFILED;
    byTag.set(key, (byTag.get(key) ?? 0) + 1);
  }
  const compartments = [...byTag.entries()].sort(([a], [b]) => a.localeCompare(b));

  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [description, setDescription] = useState(vault.description ?? '');
  useEffect(() => setDescription(vault.description ?? ''), [vault.id, vault.description]);

  const describeMutation = useMutation({
    mutationFn: (text: string) => updateMap(vault.id, { description: text }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['maps'] }),
  });
  const autoTrackMutation = useMutation({
    mutationFn: (on: boolean) => updateMap(vault.id, { autoTrack: on }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['maps'] }),
  });

  const autoCount = systems.filter((s) => s.auto_expires_at).length;

  const invalidateConnections = () => queryClient.invalidateQueries({ queryKey: ['map-connections', vault.id] });
  const removeConnectionMutation = useMutation({
    mutationFn: (connectionId: number) => removeMapConnection(vault.id, connectionId),
    onSuccess: invalidateConnections,
  });
  const updateConnectionMutation = useMutation({
    mutationFn: (v: { id: number; body: Parameters<typeof updateMapConnection>[2] }) =>
      updateMapConnection(vault.id, v.id, v.body),
    onSuccess: invalidateConnections,
  });

  const nameOf = (mapSystemId: number | null) => {
    if (!mapSystemId) return 'unknown';
    const s = systems.find((x) => x.id === mapSystemId);
    return s ? s.label || s.name : `#${mapSystemId}`;
  };

  return (
    // Side by side now that this sits under the full-width list rather than
    // in a 340px rail — the vault's own facts and its connection log are two
    // reference blocks, not a sequence to scroll through.
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: 2,
        alignItems: 'start',
      }}
    >
      <ConsolePanel title={vault.name} accent={C.wormhole} corners={false}>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <StatusBadge label={SEAL_LABEL[scopeKey] ?? scopeKey} accent={C.wormhole} />
            <Typography sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.78rem' }}>
              {systems.length} {systems.length === 1 ? 'entry' : 'entries'}
            </Typography>
          </Stack>

          {home && (
            <Row label="Home">
              <Typography sx={{ color: C.text, fontFamily: FONT.mono, fontSize: '0.85rem' }}>
                {home.label || home.name}
              </Typography>
            </Row>
          )}

          {compartments.length > 0 && (
            <Row label="Groups">
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end">
                {compartments.map(([tag, count]) => (
                  <StatusBadge key={tag} label={`${tag} · ${count}`} accent={C.cyan} />
                ))}
              </Stack>
            </Row>
          )}

          {/* Auto-tracking is opt-in per vault, and owner-only to turn on:
              once it is, every member's jumps write here. Members still see
              the state — an entry appearing on its own needs an explanation
              visible to whoever is looking at it. */}
          {isOwner ? (
            <FormControlLabel
              sx={{ ml: 0 }}
              control={
                <Switch
                  size="small"
                  checked={!!vault.auto_track}
                  onChange={(e) => autoTrackMutation.mutate(e.target.checked)}
                />
              }
              label={
                <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.72rem', color: C.textDim }}>
                  Auto-track jumps{autoCount > 0 ? ` · ${autoCount} live` : ''}
                </Typography>
              }
            />
          ) : (
            vault.auto_track === 1 && (
              <Row label="Auto-track">
                <StatusBadge label={autoCount > 0 ? `${autoCount} live · 12h` : 'on · 12h'} accent={C.gold} dim />
              </Row>
            )
          )}

          {/* The column existed from day one and nothing could write it —
              a vault's purpose ("rolling chain, do not pin anything here")
              had to live in someone's head. Owner-only, like every other
              vault setting; everyone else reads it. */}
          {isOwner ? (
            <TextField
              size="small"
              placeholder="What is this vault for?"
              multiline
              minRows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onBlur={() => description !== (vault.description ?? '') && describeMutation.mutate(description)}
            />
          ) : (
            vault.description && (
              <Typography sx={{ color: C.textDim, fontSize: '0.8rem', pt: 0.5 }}>{vault.description}</Typography>
            )
          )}
        </Stack>
      </ConsolePanel>

      {connections.length > 0 && (
        <ConsolePanel title="Logged Connections" accent={C.cyan} corners={false}>
          <Stack spacing={0.75}>
            {connections.map((c) => (
              <Stack
                key={c.id}
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                  borderBottom: `1px solid ${C.hairline}`,
                  pb: 0.75,
                  '&:last-of-type': { borderBottom: 'none', pb: 0 },
                  opacity: c.collapsed_at ? 0.45 : 1,
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    noWrap
                    sx={{
                      fontFamily: FONT.mono,
                      fontSize: '0.8rem',
                      color: C.text,
                      textDecoration: c.collapsed_at ? 'line-through' : 'none',
                    }}
                  >
                    {nameOf(c.from_map_system_id)} &rarr; {nameOf(c.to_map_system_id)}
                  </Typography>
                  <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                    {c.signature_code && (
                      <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.68rem', color: C.textDim }}>{c.signature_code}</Typography>
                    )}
                    {/* Mass is the field that changes most and changes fast —
                        one click cycles it rather than reopening a form. */}
                    <Box
                      component="button"
                      type="button"
                      title="Cycle mass status"
                      onClick={() =>
                        updateConnectionMutation.mutate({ id: c.id, body: { massStatus: nextMass(c.mass_status) } })
                      }
                      sx={{
                        font: 'inherit',
                        fontFamily: FONT.mono,
                        fontSize: '0.68rem',
                        background: 'none',
                        border: 'none',
                        p: 0,
                        cursor: 'pointer',
                        color: MASS_COLOR[c.mass_status],
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      {c.mass_status}
                    </Box>
                    <ConnectionAction
                      label={c.life_status === 'eol' ? 'EOL' : 'mark EOL'}
                      active={c.life_status === 'eol'}
                      accent={C.red}
                      onClick={() =>
                        updateConnectionMutation.mutate({
                          id: c.id,
                          body: { lifeStatus: c.life_status === 'eol' ? 'stable' : 'eol' },
                        })
                      }
                    />
                    <ConnectionAction
                      label={c.collapsed_at ? 'collapsed' : 'collapse'}
                      active={!!c.collapsed_at}
                      accent={C.textDim}
                      onClick={() => updateConnectionMutation.mutate({ id: c.id, body: { collapsed: !c.collapsed_at } })}
                    />
                  </Stack>
                </Box>
                <Box
                  role="button"
                  aria-label="Delete connection"
                  onClick={() => removeConnectionMutation.mutate(c.id)}
                  sx={{
                    display: 'flex',
                    color: C.textDim,
                    cursor: 'pointer',
                    flexShrink: 0,
                    '&:hover': { color: C.red },
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 15 }} />
                </Box>
              </Stack>
            ))}
          </Stack>
        </ConsolePanel>
      )}

      {canSeeWebhooks && (
        <ConsolePanel title="Webhooks Watching This Vault" accent={C.gold} corners={false}>
          <Stack spacing={1.25}>
            {vaultWebhooks.length === 0 ? (
              <Typography sx={{ color: C.textDim, fontSize: '0.78rem' }}>
                No webhooks scoped to this vault yet.
              </Typography>
            ) : (
              <Stack spacing={0.75}>
                {vaultWebhooks.map((h) => (
                  <Stack key={h.id} direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                    <StatusBadge label={h.label} accent={h.enabled ? C.gold : C.textDim} dim={!h.enabled} />
                    <Typography sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.68rem' }}>
                      {h.events.length} {h.events.length === 1 ? 'event' : 'events'}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}
            {/* A director can never scope a webhook to a vault (see
                requireWebhookAdmin's corp/alliance-only rule) — the shortcut
                would just land them on a scope option they can't use. */}
            {me.isAdmin && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => navigate(`/admin?tab=6&vault=${vault.id}`)}
                sx={{ alignSelf: 'flex-start', fontSize: '0.72rem' }}
              >
                Create webhook for this vault
              </Button>
            )}
          </Stack>
        </ConsolePanel>
      )}

      {canDelete && (
        <ScanFrame color={C.red} corners={false}>
          <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.68rem'), mb: 0.75 }}>Danger Zone</Typography>
          <Typography sx={{ color: C.textDim, fontSize: '0.78rem', mb: 1.25 }}>
            {isOwner
              ? 'Deletes every entry and logged connection in this vault. This cannot be undone.'
              : "You're not the owner — deleting uses your admin override, and is logged. Deletes every entry and logged connection in this vault. This cannot be undone."}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setConfirmingDelete(true)}
            sx={{ color: C.red, borderColor: hexToRgba(C.red, 0.5), '&:hover': { borderColor: C.red, backgroundColor: hexToRgba(C.red, 0.08) } }}
          >
            Delete Vault
          </Button>
        </ScanFrame>
      )}

      <AskDialog
        open={confirmingDelete}
        title={`Delete "${vault.name}"?`}
        body={`Every entry and logged connection in it goes with it — ${systems.length} ${
          systems.length === 1 ? 'entry' : 'entries'
        } and ${connections.length} ${connections.length === 1 ? 'connection' : 'connections'}. This cannot be undone.`}
        confirmLabel="Delete vault"
        destructive
        onCancel={() => setConfirmingDelete(false)}
        onConfirm={() => {
          setConfirmingDelete(false);
          onDeleteVault();
        }}
      />
    </Box>
  );
};

/** Rolling a hole is a three-stage countdown, so cycling beats a dropdown.
 * 'unknown' enters the cycle at fresh and never comes back — you don't
 * un-know a connection's mass once you've started rolling it. */
const nextMass = (m: MassStatus): MassStatus =>
  m === 'fresh' ? 'reduced' : m === 'reduced' ? 'critical' : m === 'critical' ? 'fresh' : 'fresh';

const ConnectionAction: React.FC<{ label: string; active: boolean; accent: string; onClick: () => void }> = ({
  label,
  active,
  accent,
  onClick,
}) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      font: 'inherit',
      fontFamily: FONT.mono,
      fontSize: '0.62rem',
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      cursor: 'pointer',
      px: 0.6,
      py: '1px',
      borderRadius: '3px',
      background: 'transparent',
      border: `1px ${active ? 'solid' : 'dashed'} ${hexToRgba(accent, active ? 0.8 : 0.4)}`,
      color: active ? accent : C.textDim,
      '&:hover': { borderStyle: 'solid' },
    }}
  >
    {label}
  </Box>
);

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
    <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.68rem'), pt: 0.4, flexShrink: 0 }}>{label}</Typography>
    <Box sx={{ textAlign: 'right', minWidth: 0 }}>{children}</Box>
  </Stack>
);

export default VaultSummary;
